import React from 'react';
import { Handle, Position } from 'reactflow';
import PropTypes from 'prop-types';
import { Main } from './styles';
import { IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export const MappingCard = ({data}) => {
  const { 
    area, 
    name, 
    criticity, 
    last, 
    description, 
    user, 
    processCount, 
    systemCount, 
    assetCount,
    dpia,
    lia,
    dataMapping,
    saas,
    developedInHouse,
    type,
    pii,
    location,
    isExpanded,
    onToggleExpand,
    hasChildren
  } = data;

  // Determinar cores baseadas no tipo de elemento
  const getCardColor = () => {
    switch(area) {
      case 'Área de Negócio': return '#2196f3';
      case 'Processo': return '#e91e63';
      case 'Sistema': return '#00bcd4';
      case 'Ativo': return '#4caf50';
      default: return '#666';
    }
  };

  const getCriticalityColor = () => {
    if (criticity <= 2) return '#4caf50';
    if (criticity === 3) return '#ff9800';
    return '#f44336';
  };

  return (
    <Main 
      criticity={criticity} 
      style={{
        marginInline: 'auto',
        border: `2px solid ${getCardColor()}`,
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        minWidth: '180px',
        maxWidth: '220px',
        padding: '12px'
      }}
    >
      <Handle 
        type="target" 
        position={Position.Top}
        style={{
          background: getCardColor(),
          width: '10px',
          height: '10px',
          border: '2px solid #fff'
        }}
      />
      
      <h1 style={{
        margin: '0 0 4px 0',
        fontSize: '12px',
        color: getCardColor(),
        fontWeight: 'bold',
        textTransform: 'uppercase'
      }}>{area}</h1>
      
      <h2 style={{
        margin: '0 0 8px 0',
        fontSize: '14px',
        color: '#333',
        fontWeight: 'bold',
        lineHeight: 1.2
      }}>{name}</h2>
      
      {description && (
        <p style={{
          margin: '0 0 8px 0',
          fontSize: '11px',
          color: '#666',
          lineHeight: 1.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>{description}</p>
      )}

      <hr style={{border: '1px solid #e0e0e0', margin: '8px 0'}}/>
      
      <section style={{marginBottom: '8px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <p style={{margin: 0, fontSize: '12px', color: '#666'}}>Criticidade</p>
          <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
            <div 
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: getCriticalityColor()
              }}
            />
            <span style={{fontSize: '12px', fontWeight: 'bold'}}>{criticity}</span>
          </div>
        </div>
      </section>

      {user && (
        <div style={{marginBottom: '4px'}}>
          <p style={{margin: 0, fontSize: '10px', color: '#888'}}>
            Responsável: <strong>{user}</strong>
          </p>
        </div>
      )}

      {/* Informações específicas por tipo */}
      {area === 'Área de Negócio' && (
        <div style={{fontSize: '10px', color: '#666'}}>
          {processCount > 0 && <div>Processos: {processCount}</div>}
          {systemCount > 0 && <div>Sistemas: {systemCount}</div>}
          {assetCount > 0 && <div>Ativos: {assetCount}</div>}
        </div>
      )}

      {area === 'Processo' && (
        <div style={{fontSize: '10px', color: '#666'}}>
          {dpia !== undefined && <div>DPIA: {dpia ? 'Sim' : 'Não'}</div>}
          {lia !== undefined && <div>LIA: {lia ? 'Sim' : 'Não'}</div>}
          {dataMapping > 0 && <div>Dados mapeados: {dataMapping}</div>}
        </div>
      )}

      {area === 'Sistema' && (
        <div style={{fontSize: '10px', color: '#666'}}>
          {saas !== undefined && <div>SaaS: {saas ? 'Sim' : 'Não'}</div>}
          {developedInHouse !== undefined && <div>Próprio: {developedInHouse ? 'Sim' : 'Não'}</div>}
        </div>
      )}

      {area === 'Ativo' && (
        <div style={{fontSize: '10px', color: '#666'}}>
          {type && <div>Tipo: {type}</div>}
          {pii !== undefined && <div>PII: {pii ? 'Sim' : 'Não'}</div>}
          {location && <div>Local: {location}</div>}
        </div>
      )}
      
      {hasChildren && (
        <div style={{display: 'flex', justifyContent: 'center', marginTop: '8px'}}>
          <IconButton 
            onClick={onToggleExpand}
            size="small"
            style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '50%',
              padding: '2px'
            }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </div>
      )}

      {last === undefined && (
        <Handle 
          type="source" 
          position={Position.Bottom} 
          id="a"
          style={{
            background: getCardColor(),
            width: '10px',
            height: '10px',
            border: '2px solid #fff'
          }}
        />
      )}
    </Main>
  );
};

MappingCard.propTypes = {
  data: PropTypes.shape({
    area: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    criticity: PropTypes.oneOf([1, 2, 3, 4, 5]).isRequired,
    description: PropTypes.string,
    user: PropTypes.string,
    processCount: PropTypes.number,
    systemCount: PropTypes.number,
    assetCount: PropTypes.number,
    dpia: PropTypes.bool,
    lia: PropTypes.bool,
    dataMapping: PropTypes.number,
    saas: PropTypes.bool,
    developedInHouse: PropTypes.bool,
    type: PropTypes.string,
    pii: PropTypes.bool,
    location: PropTypes.string,
    isExpanded: PropTypes.bool,
    onToggleExpand: PropTypes.func,
    hasChildren: PropTypes.bool
  })
};
